<script lang="ts" setup>
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";

export interface DropdownItem {
  value: string;
  label: string;
  icon?: string;
  alt?: string;
  index?: number;
  description?: string;
  action?: () => void;
  ariaLabel?: string;
}

interface Props {
  items: DropdownItem[];
  primary?: boolean;
  primaryText?: boolean;
  modelValue: DropdownItem;
  ariaLabel?: string;
  label?: string;
  alignLeft?: boolean;
  center?: boolean;
  showCheck?: boolean;
  size?: "sm" | "md" | "lg";
  openToTop?: boolean;
  boxClass?: string;
  outlined?: boolean;
  minWidth?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  clazz?: string;
}

defineProps<Props>();

const emit = defineEmits(["update:modelValue"]);

function selectItem(item: DropdownItem) {
  item.action?.();
  emit("update:modelValue", item);
}

// headlessui forces tabindex=-1 on options, keep them reachable by Tab
const vTabbable = {
  mounted: (el: HTMLElement) => (el.tabIndex = 0),
  updated: (el: HTMLElement) => (el.tabIndex = 0),
};
</script>

<template>
  <Listbox
    v-slot="{ open }"
    :modelValue="modelValue"
    :disabled
    @update:modelValue="selectItem"
    as="div"
    class="group"
  >
    <ListboxLabel
      v-if="label"
      class="not-sr-only"
      :class="fullWidth ? 'text-sm' : 'text-xs'"
    >
      <div class="mb-2">{{ label }}</div>
    </ListboxLabel>

    <!-- headlessui swallows Tab inside the listbox; let it move focus between options -->
    <div class="relative" @keydown.tab.capture.stop>
      <div
        class="inline-flex rounded-md shadow-sm"
        :class="{ 'w-full': fullWidth }"
      >
        <!-- Button (Headless UI handles keyboard + focus) -->
        <ListboxButton
          :ariaLabel
          class="cursor-pointer"
          :class="[
            {
              'border-inset inline-flex items-center justify-between gap-x-2 rounded-md border border-transparent bg-gray-600 px-2 py-2 text-gray-300 shadow-sm group-hover:border-gray-500':
                !$slots.label,
              '!bg-primary-500': primary && !outlined && !$slots.label,
              'border-primary-500! !bg-transparent':
                primary && outlined && !$slots.label,
              'h-6!': size === 'sm',
              'h-8!': size === 'md',
              'h-9!': size === 'lg',
              'w-full': fullWidth,
            },
            clazz,
          ]"
        >
          <slot name="label" v-bind="{ open }">
            <div class="flex items-center gap-x-2.5">
              <ficon
                v-if="showCheck"
                icon="fa-solid fa-check"
                class="-ml-0.5 size-5 text-gray-300"
                :class="{ 'text-primary-500!': primaryText && !$slots.label }"
                aria-hidden="true"
              />
              <ficon
                v-if="modelValue?.icon"
                :icon="modelValue?.icon"
                class="-ml-0.5 size-5 text-xs text-gray-300"
                :class="{ 'text-primary-500!': primaryText && !$slots.label }"
                aria-hidden="true"
              />

              <p
                class="min-w-5 overflow-hidden text-sm font-normal text-ellipsis text-white"
                :class="{ 'text-primary-400!': primaryText && !$slots.label }"
              >
                {{ modelValue?.label }}
              </p>

              <slot name="selectedTag" />
            </div>

            <ficon
              icon="fa-solid fa-chevron-down"
              class="ui-open:rotate-180 size-3 text-gray-300 transition-all group-hover:text-gray-200"
              :class="{ 'text-primary-500!': primaryText && !$slots.label }"
              aria-hidden="true"
            />
          </slot>
        </ListboxButton>
      </div>

      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          class="ring-opacity-5 absolute right-0 z-50 mt-1 origin-top-right divide-y divide-gray-500 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black focus:outline-none"
          :class="[
            openToTop
              ? 'bottom-full mb-1 origin-bottom-right'
              : 'mt-1 origin-top-right',
            alignLeft ? 'left-[25%]' : '',
            boxClass,
          ]"
        >
          <ListboxOption
            v-for="item in items"
            :key="item.value"
            :value="item"
            as="template"
            v-slot="{ active, selected }"
          >
            <li
              v-tabbable
              class="min-w-24 cursor-pointer p-3 text-xs select-none"
              :class="[
                selected
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-600 text-gray-300',
                active ? 'bg-primary-600 text-white' : '',
                size === 'sm' ? 'p-2' : '',
                !fullWidth ? 'max-w-32' : '',
                minWidth,
              ]"
              :aria-label="item.ariaLabel"
            >
              <slot name="option" v-bind="{ item }">
                <div class="flex w-full items-center justify-between">
                  <div class="flex items-center gap-x-2.5">
                    <ficon
                      v-if="item.icon?.startsWith('fa')"
                      :icon="item.icon"
                      class="size-4"
                    />
                    <img
                      v-else-if="item.icon"
                      :src="item.icon"
                      :alt="item.alt ?? ''"
                      class="size-4"
                    />

                    <span :class="selected ? 'font-semibold' : 'font-normal'">
                      {{ item.label }}
                    </span>

                    <slot name="tag" :item="item" />
                  </div>

                  <ficon
                    v-if="
                      item.value === modelValue.value &&
                      (showCheck === undefined || showCheck === true)
                    "
                    icon="fa-solid fa-check"
                    class="size-3 text-white"
                    aria-hidden="true"
                  />
                </div>
              </slot>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>
